/* import __COLOCATED_TEMPLATE__ from './treemap.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import ENV from 'embercom/config/environment';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import type {
  SeriesTreemapOptions,
  Options,
  DataLabelsOptions,
  AxisLabelsFormatterContextObject,
  Point,
  Chart,
} from 'highcharts';
import { getNumericValue } from 'embercom/lib/reporting/chart-data-resource-compatible';
import type { MetricValue } from 'embercom/lib/reporting/chart-data-resource';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { inject as service } from '@ember/service';
import {
  colorForNoMetricValue,
  metricConfigs,
  type MetricId,
  addTopicFilter,
  getComputedCssVar,
  topicPrefix,
  layoutColorAxisLabels,
} from './common';
import type {
  Topic,
  TopicTaxonomy,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import type ThemeService from 'embercom/services/theme-service';
import PALETTE from '@intercom/pulse/lib/palette';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Range from 'embercom/models/reporting/range';
import { cached } from 'tracked-toolbox';
import { pick } from 'underscore';

interface Args {
  reportState: ReportState;
  limit: number;
  metricId: MetricId;
  topic: Topic | null;
  topicTaxonomy?: TopicTaxonomy;
  onTopicSelected: (topicId: string | null) => void;
  onShowConversationExplorer: (
    topic: string,
    subtopic: string | null,
    dateRange: Range,
    conversationCount: number,
  ) => void;
}

interface TreemapPoint extends Point {
  id: string;
  metricValue: MetricValue;
  value: number;
  colorValue: number | undefined;
  isSubtopic: boolean;
}

export default class Treemap extends Component<Args> {
  @service declare reportingMetrics: ReportingMetrics;
  @service declare themeService: ThemeService;
  @service declare intl: IntlService;

  @tracked tooltipOpenerElement: HTMLElement | undefined = undefined;
  @tracked hoveredPoint: Point | null = null;
  @tracked hideTooltip = false;
  lastHoveredElement: HTMLElement | undefined = undefined;

  tooltipShowDelay = ENV.APP._500MS;
  tooltipHideDelay = ENV.APP._100MS;

  @use topicsData = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  @cached
  get metricConfig() {
    return metricConfigs(this.intl)[this.args.metricId];
  }

  get chartDefinition() {
    if (this.args.topic === null) {
      return this.topicsChartDefinition;
    }
    return this.subtopicsChartDefinition;
  }

  get topicsChartDefinition() {
    return {
      view_by: 'conversation_custom_fields#ai_insights_topic',
      view_by_display_limit: 500,
      chart_series: [{ metric_id: 'v1.new_conversations' }, this.metricConfig.seriesConfig],
    };
  }

  get subtopicsChartDefinition() {
    if (this.args.topic === null) {
      throw new Error('topic is required for subtopic chart definition');
    }
    return {
      view_by: 'conversation_custom_fields#ai_insights_topic_subtopic',
      view_by_display_limit: 500,
      chart_series: [
        addTopicFilter({ metric_id: 'v1.new_conversations' }, this.args.topic.name),
        addTopicFilter(this.metricConfig.seriesConfig, this.args.topic.name),
      ],
    };
  }

  get highchartsOptions(): Options {
    let stops: Array<[number, string]> = this.metricConfig.colorAxis.stops.map(
      ([index, paletteColor]) => [index, PALETTE[paletteColor]],
    );

    // noop to force recalculation when the theme changes
    this.themeService.darkModeEnabled;

    let component = this;
    return {
      chart: {
        margin: 0,
        marginBottom: 50,
        events: {
          render: (args: any) => {
            layoutColorAxisLabels(args.target as Chart);
            return true;
          },
        },
      },
      legend: {
        symbolRadius: 6, // makes the colorAxis have rounded corners
        align: 'left',
        x: 0,
        y: 25,
      },
      colorAxis: {
        showInLegend: true,
        className: 'font-mono font-medium',
        gridLineColor: 'transparent',
        showFirstLabel: true,
        showLastLabel: true,
        tickPositions: [
          this.extremes.minMetricValue,
          this.extremes.minMetricValue + 1,
          this.extremes.maxMetricValue,
        ],
        stops,
        labels: {
          useHTML: false,
          align: 'left',
          step: 2,
          style: {
            fontSize: '12px',
            color: 'var(--text-default)',
          },
          formatter(this: AxisLabelsFormatterContextObject) {
            return component.topicsData.formatValue({
              value: this.value as any,
              metricId: component.args.metricId,
              aggregation: component.metricConfig.seriesConfig?.aggregation,
              durationFormat: 'hours',
            });
          },
        },
      },
      tooltip: { enabled: false },
      plotOptions: {
        series: {
          point: {
            events: {
              click(this: Point & { id: string }) {
                if (component.args.topic === null) {
                  component.args.onTopicSelected(this.id);
                }
              },
              mouseOver(this: Point, event: PointerEvent) {
                if (!component.hideTooltip) {
                  // temporarily hide the tooltip so that the showDelay works as expected,
                  // this is necessary because we're reusing the same tooltip for all elements
                  component.hideTooltip = true;
                  setTimeout(() => {
                    component.hideTooltip = false;
                    component.tooltipOpenerElement = component.lastHoveredElement;
                  }, ENV.APP._10MS);
                }
                component.hoveredPoint = this;
                component.lastHoveredElement = (event.target as any).graphic.element;
                return false;
              },
              mouseOut(this: Point, _event: PointerEvent) {
                component.tooltipOpenerElement = undefined;
                return true;
              },
            },
          },
          states: {
            hover: {
              borderColor: 'white',
              brightness: 0.1,
            },
          },
        },
      },
    };
  }

  buildChartDataItem(id: string, metricValues: MetricValue[]): TreemapPoint {
    let [volume, metricValue] = metricValues;
    let [topicName, subtopicName] = id.split('=>');
    let colorValue = getNumericValue(metricValue) ?? undefined;
    return {
      id,
      name: subtopicName ?? topicName,
      value: getNumericValue(volume) || 0,
      colorValue,
      metricValue,
      isSubtopic: subtopicName !== undefined,
    } as TreemapPoint;
  }

  buildChartData(data: Record<string, MetricValue[]>) {
    return Object.entries(data)
      .map(([id, metricValues]) => this.buildChartDataItem(id, metricValues))
      .sort((a, b) => b.value - a.value);
  }

  @cached
  get data() {
    let topicsData = this.topicsData.flatCombinedData;
    if (this.args.topic) {
      // Filter to only the subtopics that start with the chosen topic
      let prefix = topicPrefix(this.args.topic.name);
      topicsData = pick(this.topicsData.flatCombinedData, (_value, key) => {
        return key.startsWith(prefix);
      }) as Record<string, MetricValue[]>;
    }
    return this.buildChartData(topicsData).slice(0, this.args.limit);
  }

  @cached
  get extremes() {
    let validValues = this.data
      .map((point) => point.colorValue)
      .filter((value) => value !== undefined) as number[];
    return {
      minMetricValue: Math.floor(Math.min(...validValues)),
      maxMetricValue: Math.ceil(Math.max(...validValues)),
    };
  }

  get highchartsSeries(): Array<
    SeriesTreemapOptions & {
      dataLabels: DataLabelsOptions;
    }
  > {
    let component = this;

    // noop to force recalculation when the theme changes
    this.themeService.darkModeEnabled;

    return [
      {
        type: 'treemap',
        color: colorForNoMetricValue,
        allowTraversingTree: true,
        turboThreshold: 0,
        animation: false,
        cursor: this.args.topic ? undefined : 'pointer',
        levels: [
          {
            level: 1,
            borderWidth: 4,
            borderColor: getComputedCssVar('--base-module'),
            dataLabels: { enabled: true },
          },
        ],
        borderRadius: 8,
        dataLabels: {
          align: 'left',
          verticalAlign: 'top',
          padding: 14,
          style: {
            textOverflow: 'ellipsis',
            textOutline: '0',
            fontSize: '12px',
            fontWeight: 'normal',
          },
          enabled: true,
          crop: true,
          useHTML: true,
          overflow: 'justify',
          formatter(this: TreemapPoint) {
            let formattedMetricValue = component.topicsData.formatValue({
              value: this.metricValue,
              metricId: component.args.metricId,
              aggregation: component.metricConfig.seriesConfig?.aggregation,
              durationFormat: 'hours',
            });
            let conversationCountLabel = component.intl.t(
              'components.reporting.automation.fin-insights.analyse.topics-explorer.treemap.conversation-count',
              { count: this.value },
            );
            if (this.shapeArgs?.height < 65) {
              return `            <div class="flex flex-col w-full truncate overflow-hidden">
              <div class="flex flex-row truncate mb-1.5 mt-0.5 font-semibold">
                <Text @styling="strong" class="truncate text-sm">
                  ${this.name}
                </Text>
              </div>
              </div>
            `;
            } else {
              return `            <div class="flex flex-col w-full truncate overflow-hidden">
              <div class="flex flex-row truncate mb-1.5 mt-0.5 font-semibold">
                <Text @styling="strong" class="truncate text-sm">
                  ${this.name}
                </Text>
              </div>
              <div class="flex flex-row truncate font-medium uppercase">
                <Text class="font-mono text-10px truncate">
                  ${conversationCountLabel}
                </Text>
              </div>
              <div class="flex flex-row truncate font-medium uppercase">
                <Text class="font-mono text-10px truncate">
                  ${formattedMetricValue} ${component.metricConfig.label}
                </Text>
              </div>
            </div>
              `;
            }
          },
        },
        layoutAlgorithm: 'squarified',
        layoutStartingDirection: 'horizontal',
        data: this.data,
      },
    ];
  }

  get topicName() {
    return this.args.topic ? this.args.topic.name : this.hoveredPoint?.name || '';
  }

  get subtopicName() {
    return this.args.topic ? this.hoveredPoint?.name || null : null;
  }

  get tooltipDescription() {
    let topicDescription = this.args.topicTaxonomy?.[this.topicName]?.description || null;
    return this.args.topic ? null : topicDescription;
  }

  get conversationCount() {
    return this.hoveredPoint?.value ?? 0;
  }

  get tooltipMetricValues(): { label: string; value: number | string }[] {
    if (!this.hoveredPoint) {
      return [];
    }

    let point = this.hoveredPoint as TreemapPoint;
    let formattedMetricValue = this.topicsData.formatValue({
      value: point.metricValue,
      metricId: this.args.metricId,
      aggregation: this.metricConfig.seriesConfig?.aggregation,
      durationFormat: 'hours',
    });

    return [
      {
        label: this.intl.t('components.reporting.custom.report.conversation-stream.volume'),
        value: this.conversationCount,
      },
      {
        label: this.metricConfig.label,
        value: formattedMetricValue,
      },
    ];
  }

  @action
  openConversationExplorer() {
    this.args.onShowConversationExplorer(
      this.topicName,
      this.subtopicName,
      this.args.reportState.dateRange,
      this.conversationCount,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicsExplorer::Treemap': typeof Treemap;
  }
}
